/* ==========================================================================
   BODY WISE — navigator
   Self-contained header behaviour: dropdowns, mobile drawer, search drawer,
   cart drawer and the two localization menus.

   It deliberately shares no code with the theme's own header so the two can sit
   side by side while the design is being agreed. Routes arrive as data
   attributes rather than the theme's global `routes` object, for the same reason.
   ========================================================================== */
(function () {
  'use strict';

  // The footer loads this file too, so on a page carrying both sections the
  // browser runs it twice. Everything below keeps module-level state, so the
  // second run has to be a no-op rather than a second set of listeners.
  if (window.__vnavReady) return;
  window.__vnavReady = true;

  /* ---------- small shared helpers ---------- */

  var openPanel = null;        // the panel currently holding the screen
  var lastFocus = null;
  var scrollLocked = false;

  function lockScroll(on) {
    if (on === scrollLocked) return;
    scrollLocked = on;
    document.documentElement.classList.toggle('vnav-locked', on);
    if (on) {
      document.body.style.paddingRight = scrollbarWidth() + 'px';
    } else {
      document.body.style.paddingRight = '';
    }
  }

  function scrollbarWidth() {
    var w = window.innerWidth - document.documentElement.clientWidth;
    return w > 0 ? w : 0;
  }

  function focusables(el) {
    return Array.prototype.filter.call(
      el.querySelectorAll('a[href],button:not([disabled]),input:not([disabled]),select,textarea,[tabindex]:not([tabindex="-1"])'),
      function (n) { return n.offsetParent !== null || n === document.activeElement; }
    );
  }

  function trap(e, panel) {
    if (e.key !== 'Tab') return;
    var f = focusables(panel);
    if (!f.length) return;
    var first = f[0], last = f[f.length - 1];
    if (e.shiftKey && document.activeElement === first) { e.preventDefault(); last.focus(); }
    else if (!e.shiftKey && document.activeElement === last) { e.preventDefault(); first.focus(); }
  }

  function open(panel, trigger) {
    if (openPanel && openPanel !== panel) close(openPanel);
    lastFocus = trigger || document.activeElement;
    panel.classList.add('is-open');
    panel.removeAttribute('hidden');
    panel.setAttribute('aria-hidden', 'false');
    if (trigger) trigger.setAttribute('aria-expanded', 'true');
    openPanel = panel;
    lockScroll(true);
    // let the transition start before focus, or Safari jumps the layout
    requestAnimationFrame(function () {
      var f = focusables(panel);
      if (f.length) f[0].focus();
    });
  }

  function close(panel) {
    if (!panel) return;
    panel.classList.remove('is-open');
    panel.setAttribute('aria-hidden', 'true');
    var trigger = document.querySelector('[aria-controls="' + panel.id + '"][aria-expanded="true"]');
    if (trigger) trigger.setAttribute('aria-expanded', 'false');
    if (openPanel === panel) {
      openPanel = null;
      lockScroll(false);
      if (lastFocus && document.contains(lastFocus)) lastFocus.focus();
    }
    // keep it out of the tab order once the transition has finished
    window.setTimeout(function () {
      if (!panel.classList.contains('is-open')) panel.setAttribute('hidden', '');
    }, 320);
  }

  function money(cents, format) {
    var v = (cents / 100).toFixed(2);
    // the shop's money format is passed through from Liquid; fall back to plain
    if (!format) return '$' + v;
    return format.replace(/\{\{\s*amount\s*\}\}/, v)
                 .replace(/\{\{\s*amount_no_decimals\s*\}\}/, Math.round(cents / 100))
                 .replace(/\{\{\s*amount_with_comma_separator\s*\}\}/, v.replace('.', ','));
  }

  function esc(s) {
    return String(s == null ? '' : s)
      .replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;').replace(/'/g, '&#39;');
  }

  /* ---------- panel relocation ----------
     The panels are position:fixed. Any ancestor with a transform, filter,
     perspective or containment silently turns that into position:absolute against
     *that* ancestor — the sheet then takes the header's height instead of the
     viewport's, so the body never overflows and nothing scrolls. An ancestor with
     overflow:hidden clips them the same way. Moving the panels to <body> removes
     the dependency on whatever the theme does above this section. */

  function relocatePanels(root) {
    var owner = root.getAttribute('data-vnav-id') || '';
    // the theme editor re-renders the section; drop the host we parked earlier
    if (owner) {
      var stale = document.querySelectorAll('body > [data-vnav-portal="' + owner + '"]');
      Array.prototype.forEach.call(stale, function (h) { h.parentNode.removeChild(h); });
    }

    var panels = root.querySelectorAll('.vnav-panel');
    if (!panels.length) return;

    // Every rule in vip-nav.css is scoped under .vnav, and the custom properties
    // live in the root's style attribute — so the panels cannot simply be dropped
    // on <body>, or they lose all of it. They move inside a host that carries the
    // same classes and the same inline properties, laid out so it paints nothing.
    var host = document.createElement('div');
    host.setAttribute('style', root.getAttribute('style') || '');
    host.className = root.className;
    host.setAttribute('data-vnav-portal', owner);
    host.style.position = 'static';
    host.style.zIndex = 'auto';
    host.style.height = '0';
    host.style.padding = '0';
    host.style.background = 'none';
    document.body.appendChild(host);

    root.vnavPanels = [];
    Array.prototype.forEach.call(panels, function (p) {
      host.appendChild(p);
      root.vnavPanels.push(p);
    });
  }

  function panelIn(root, selector) {
    var list = root.vnavPanels || [];
    for (var i = 0; i < list.length; i++) {
      if (list[i].matches(selector)) return list[i];
    }
    return root.querySelector(selector);
  }

  /* ---------- desktop dropdowns ---------- */

  function initDropdowns(root) {
    var items = root.querySelectorAll('[data-vnav-item]');
    if (!items.length) return;
    var fine = !window.matchMedia || window.matchMedia('(hover: hover) and (pointer: fine)').matches;
    var closeTimer = null;

    function show(item) {
      window.clearTimeout(closeTimer);
      Array.prototype.forEach.call(items, function (o) {
        if (o !== item) hide(o);
      });
      item.classList.add('is-open');
      var btn = item.querySelector('[data-vnav-trigger]');
      if (btn) btn.setAttribute('aria-expanded', 'true');
      root.classList.add('vnav--dropdown-open');
    }
    function hide(item) {
      item.classList.remove('is-open');
      var btn = item.querySelector('[data-vnav-trigger]');
      if (btn) btn.setAttribute('aria-expanded', 'false');
      if (!root.querySelector('[data-vnav-item].is-open')) root.classList.remove('vnav--dropdown-open');
    }
    function hideAllSoon() {
      window.clearTimeout(closeTimer);
      closeTimer = window.setTimeout(function () {
        Array.prototype.forEach.call(items, hide);
      }, 120);
    }

    Array.prototype.forEach.call(items, function (item) {
      var panel = item.querySelector('[data-vnav-panel]');
      var btn = item.querySelector('[data-vnav-trigger]');
      if (!panel || !btn) return;

      if (fine) {
        item.addEventListener('mouseenter', function () { show(item); });
        item.addEventListener('mouseleave', hideAllSoon);
      }

      // click always works, which also covers touch and keyboard
      btn.addEventListener('click', function (e) {
        e.preventDefault();
        if (item.classList.contains('is-open')) hide(item); else show(item);
      });

      item.addEventListener('keydown', function (e) {
        if (e.key === 'Escape' && item.classList.contains('is-open')) {
          hide(item);
          btn.focus();
        }
      });

      // focus moving out of the whole item closes it
      item.addEventListener('focusout', function (e) {
        if (!item.contains(e.relatedTarget)) hide(item);
      });
    });

    document.addEventListener('click', function (e) {
      if (!root.contains(e.target)) Array.prototype.forEach.call(items, hide);
    });
  }

  /* ---------- localization popovers ---------- */

  // These are bound per document rather than per nav, because the same markup
  // is used by the footer, which is its own section and carries no [data-vnav].
  function closeLoc(wrap) {
    wrap.classList.remove('is-open');
    var b = wrap.querySelector('[data-vnav-loc-trigger]');
    if (b) b.setAttribute('aria-expanded', 'false');
  }

  function initLocalization() {
    var wraps = document.querySelectorAll('[data-vnav-loc]');

    Array.prototype.forEach.call(wraps, function (wrap) {
      if (wrap.dataset.vnavLocReady === '1') return;   // panels get re-scanned after relocating
      wrap.dataset.vnavLocReady = '1';

      var btn = wrap.querySelector('[data-vnav-loc-trigger]');
      var list = wrap.querySelector('[data-vnav-loc-list]');
      var form = wrap.querySelector('form');
      // {% form 'localization' %} writes form_type, utf8, _method and return_to
      // ahead of the block's own fields, so the first hidden input is never ours.
      var input = wrap.querySelector('input[name="country_code"], input[name="locale_code"]');
      if (!btn || !list || !input || !form) return;

      btn.addEventListener('click', function (e) {
        e.preventDefault();
        var open = wrap.classList.toggle('is-open');
        btn.setAttribute('aria-expanded', open ? 'true' : 'false');
        if (open) {
          // close every other selector on the page so two never overlap
          Array.prototype.forEach.call(document.querySelectorAll('[data-vnav-loc].is-open'), function (o) {
            if (o !== wrap) closeLoc(o);
          });
        }
      });

      list.addEventListener('click', function (e) {
        var opt = e.target.closest('[data-value]');
        if (!opt) return;
        e.preventDefault();
        input.value = opt.getAttribute('data-value');
        form.submit();
      });

      wrap.addEventListener('keydown', function (e) {
        if (e.key === 'Escape') { closeLoc(wrap); btn.focus(); }
      });
    });

    if (initLocalization.bound) return;
    initLocalization.bound = true;
    document.addEventListener('click', function (e) {
      Array.prototype.forEach.call(document.querySelectorAll('[data-vnav-loc].is-open'), function (wrap) {
        if (!wrap.contains(e.target)) closeLoc(wrap);
      });
    });
  }

  /* ---------- mobile drawer ---------- */

  function initDrawer(root) {
    var drawer = panelIn(root, '[data-vnav-drawer]');
    if (!drawer) return;
    var triggers = root.querySelectorAll('[aria-controls="' + drawer.id + '"]');

    Array.prototype.forEach.call(triggers, function (t) {
      t.addEventListener('click', function (e) {
        e.preventDefault();
        if (drawer.classList.contains('is-open')) close(drawer); else open(drawer, t);
      });
    });

    drawer.addEventListener('click', function (e) {
      if (e.target.hasAttribute('data-vnav-close') || e.target.closest('[data-vnav-close]')) {
        e.preventDefault();
        close(drawer);
      }
      var acc = e.target.closest('[data-vnav-acc]');
      if (acc) {
        e.preventDefault();
        var li = acc.parentElement;
        var sub = li.querySelector('.vnav-drawer__sub');
        // measure before opening so max-height has a real target to animate to
        if (sub) {
          var inner = sub.firstElementChild || sub;
          sub.style.setProperty('--sub-h', inner.scrollHeight + 'px');
        }
        var opened = li.classList.toggle('is-open');
        acc.setAttribute('aria-expanded', opened ? 'true' : 'false');
      }
    });
  }

  /* ---------- search drawer ---------- */

  function initSearch(root) {
    var panel = panelIn(root, '[data-vnav-search]');
    if (!panel) return;
    var triggers = root.querySelectorAll('[aria-controls="' + panel.id + '"]');
    var input = panel.querySelector('[data-vnav-search-input]');
    var out = panel.querySelector('[data-vnav-search-results]');
    var empty = panel.querySelector('[data-vnav-search-empty]');
    var url = root.getAttribute('data-predictive-url') || '/search/suggest.json';
    var fmt = root.getAttribute('data-money-format') || '';
    var t = null, lastQ = '';

    Array.prototype.forEach.call(triggers, function (tr) {
      tr.addEventListener('click', function (e) {
        e.preventDefault();
        if (panel.classList.contains('is-open')) close(panel); else open(panel, tr);
      });
    });

    panel.addEventListener('click', function (e) {
      if (e.target.hasAttribute('data-vnav-close') || e.target.closest('[data-vnav-close]')) {
        e.preventDefault(); close(panel);
      }
    });

    if (!input || !out) return;

    input.addEventListener('input', function () {
      var q = input.value.trim();
      window.clearTimeout(t);
      if (q.length < 2) { out.innerHTML = ''; panel.classList.remove('has-results'); return; }
      t = window.setTimeout(function () { run(q); }, 220);
    });

    function run(q) {
      if (q === lastQ) return;
      lastQ = q;
      var qs = url + '?q=' + encodeURIComponent(q) +
        '&resources[type]=product,collection,page' +
        '&resources[limit]=5&resources[limit_scope]=each';
      fetch(qs, { headers: { 'Accept': 'application/json' } })
        .then(function (r) { return r.ok ? r.json() : Promise.reject(r); })
        .then(function (data) {
          if (input.value.trim() !== q) return;   // a newer keystroke already won
          render(data.resources && data.resources.results ? data.resources.results : {});
        })
        .catch(function () {
          out.innerHTML = '';
          panel.classList.remove('has-results');
        });
    }

    function render(res) {
      var html = '';
      var products = res.products || [];
      var collections = res.collections || [];
      var pages = res.pages || [];

      if (products.length) {
        html += '<div class="vnav-sr__group"><p class="vnav-sr__label">Products</p><ul class="vnav-sr__products">';
        products.forEach(function (p) {
          var img = p.featured_image && p.featured_image.url ? p.featured_image.url : '';
          html += '<li><a href="' + esc(p.url) + '">' +
            (img ? '<span class="vnav-sr__thumb"><img src="' + esc(img) + '" alt="" loading="lazy"></span>'
                 : '<span class="vnav-sr__thumb"></span>') +
            '<span class="vnav-sr__meta"><span class="vnav-sr__title">' + esc(p.title) + '</span>' +
            (p.price != null ? '<span class="vnav-sr__price">' + esc(money(p.price, fmt)) + '</span>' : '') +
            '</span></a></li>';
        });
        html += '</ul></div>';
      }

      [['Collections', collections], ['Pages', pages]].forEach(function (pair) {
        if (!pair[1].length) return;
        html += '<div class="vnav-sr__group"><p class="vnav-sr__label">' + pair[0] + '</p><ul class="vnav-sr__links">';
        pair[1].forEach(function (i) {
          html += '<li><a href="' + esc(i.url) + '">' + esc(i.title) + '</a></li>';
        });
        html += '</ul></div>';
      });

      out.innerHTML = html;
      panel.classList.toggle('has-results', html !== '');
      if (empty) empty.hidden = html !== '';
    }
  }

  /* ---------- cart drawer ---------- */

  function initCart(root) {
    var panel = panelIn(root, '[data-vnav-cart]');
    if (!panel) return;
    var triggers = root.querySelectorAll('[aria-controls="' + panel.id + '"]');
    var body = panel.querySelector('[data-vnav-cart-body]');
    var footer = panel.querySelector('[data-vnav-cart-footer]');
    var countEls = root.querySelectorAll('[data-vnav-cart-count]');
    var cartUrl = root.getAttribute('data-cart-url') || '/cart';
    var changeUrl = root.getAttribute('data-cart-change-url') || '/cart/change';
    var fmt = root.getAttribute('data-money-format') || '';
    var loaded = false;

    Array.prototype.forEach.call(triggers, function (tr) {
      tr.addEventListener('click', function (e) {
        e.preventDefault();
        if (panel.classList.contains('is-open')) { close(panel); return; }
        open(panel, tr);
        if (!loaded) load();
      });
    });

    panel.addEventListener('click', function (e) {
      if (e.target.hasAttribute('data-vnav-close') || e.target.closest('[data-vnav-close]')) {
        e.preventDefault(); close(panel); return;
      }
      var step = e.target.closest('[data-vnav-qty]');
      if (step) {
        e.preventDefault();
        var line = parseInt(step.getAttribute('data-line'), 10);
        var qty = parseInt(step.getAttribute('data-vnav-qty'), 10);
        change(line, qty);
      }
    });

    // vip-pack's quick add fires these after a successful /cart/add
    ['cart:refresh', 'cart:build'].forEach(function (evt) {
      document.addEventListener(evt, function () { load(); });
      document.documentElement.addEventListener(evt, function () { load(); });
    });

    function change(line, quantity) {
      panel.classList.add('is-busy');
      fetch(changeUrl + '.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
        body: JSON.stringify({ line: line, quantity: quantity })
      })
        .then(function (r) { return r.ok ? r.json() : Promise.reject(r); })
        .then(paint)
        .catch(function () { window.location.href = cartUrl; })
        .then(function () { panel.classList.remove('is-busy'); });
    }

    function load() {
      return fetch(cartUrl + '.js', { headers: { 'Accept': 'application/json' } })
        .then(function (r) { return r.ok ? r.json() : Promise.reject(r); })
        .then(function (c) { loaded = true; paint(c); })
        .catch(function () { /* leave whatever is on screen */ });
    }

    function paint(cart) {
      Array.prototype.forEach.call(countEls, function (el) {
        el.textContent = cart.item_count;
        el.classList.toggle('is-empty', cart.item_count === 0);
      });
      if (!body) return;

      if (!cart.items || !cart.items.length) {
        body.innerHTML = '<p class="vnav-cart__empty">' + esc(panel.getAttribute('data-empty-text') || 'Your cart is empty.') + '</p>';
        if (footer) footer.hidden = true;
        return;
      }

      var html = '<ul class="vnav-cart__items">';
      cart.items.forEach(function (it, i) {
        var line = i + 1;
        var img = it.image ? it.image.replace(/(\.[a-z]+)(\?|$)/i, '_200x$1$2') : '';
        html += '<li class="vnav-cart__item">' +
          '<a class="vnav-cart__thumb" href="' + esc(it.url) + '">' +
            (img ? '<img src="' + esc(img) + '" alt="" loading="lazy">' : '') +
          '</a>' +
          '<div class="vnav-cart__meta">' +
            '<a class="vnav-cart__title" href="' + esc(it.url) + '">' + esc(it.product_title) + '</a>' +
            (it.variant_title ? '<p class="vnav-cart__variant">' + esc(it.variant_title) + '</p>' : '') +
            (it.selling_plan_allocation ? '<p class="vnav-cart__plan">' + esc(it.selling_plan_allocation.selling_plan.name) + '</p>' : '') +
            '<div class="vnav-cart__row">' +
              '<div class="vnav-cart__qty">' +
                '<button type="button" data-vnav-qty="' + (it.quantity - 1) + '" data-line="' + line + '" aria-label="Decrease quantity">&minus;</button>' +
                '<span>' + it.quantity + '</span>' +
                '<button type="button" data-vnav-qty="' + (it.quantity + 1) + '" data-line="' + line + '" aria-label="Increase quantity">+</button>' +
              '</div>' +
              '<span class="vnav-cart__price">' + esc(money(it.final_line_price, fmt)) + '</span>' +
            '</div>' +
            '<button type="button" class="vnav-cart__remove" data-vnav-qty="0" data-line="' + line + '">Remove</button>' +
          '</div>' +
        '</li>';
      });
      html += '</ul>';
      body.innerHTML = html;

      if (footer) {
        footer.hidden = false;
        var total = footer.querySelector('[data-vnav-cart-total]');
        if (total) total.textContent = money(cart.total_price, fmt);
      }
    }

    // keep the badge honest on first paint without opening anything
    load();
  }

  /* ---------- sticky ---------- */

  function initSticky(root) {
    if (root.getAttribute('data-sticky') === 'false') return;
    var last = 0;
    var onScroll = function () {
      var y = window.pageYOffset || document.documentElement.scrollTop;
      root.classList.toggle('vnav--scrolled', y > 8);
      if (root.getAttribute('data-hide-on-scroll') === 'true') {
        root.classList.toggle('vnav--tucked', y > last && y > 160 && !openPanel);
      }
      last = y;
    };
    window.addEventListener('scroll', onScroll, { passive: true });
    onScroll();
  }

  /* ---------- boot ---------- */

  function initNav(root) {
    if (!root || root.dataset.vnavReady === '1') return;
    root.dataset.vnavReady = '1';
    try { relocatePanels(root); }
    catch (e) { if (window.console && console.error) console.error('[vnav] relocatePanels:', e); }

    [initDropdowns, initDrawer, initSearch, initCart, initSticky]
      .forEach(function (fn) {
        try { fn(root); }
        catch (e) { if (window.console && console.error) console.error('[vnav] ' + fn.name + ':', e); }
      });
  }

  function boot() {
    document.querySelectorAll('[data-vnav]').forEach(function (n) { initNav(n); });
    // after initNav, so panels moved onto <body> are picked up in place
    try { initLocalization(); }
    catch (e) { if (window.console && console.error) console.error('[vnav] initLocalization:', e); }
  }

  document.addEventListener('keydown', function (e) {
    if (e.key === 'Escape' && openPanel) close(openPanel);
    if (openPanel) trap(e, openPanel);
  });

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', boot);
  else boot();

  // theme editor re-renders the section on every setting change
  document.addEventListener('shopify:section:load', boot);
  document.addEventListener('shopify:section:unload', function () { lockScroll(false); });
})();
